import React from 'react';

const VietnamAILaw = () => (
  <article className="news-article-body">
    <p className="news-lead">
      On <strong>March 1, 2026</strong>, Vietnam's comprehensive AI law came into force —
      making it the <strong>first country in Southeast Asia</strong> to enact dedicated AI
      legislation with binding legal obligations. Passed by the National Assembly in December
      2025, the law applies to both Vietnamese and foreign entities operating in Vietnam and
      covers everything from generative AI content labelling to mandatory human oversight
      of high-risk AI systems.
    </p>

    <h2>Why Vietnam Moved First</h2>
    <p>
      Vietnam's ambition to become a regional digital economy hub — with government targets
      for double-digit digital economy growth — created both the urgency and the political
      will to legislate. Policymakers recognised that attracting foreign AI investment and
      building local AI industry required a clear, predictable legal environment.
      Additionally, Vietnam has experienced significant deepfake fraud and AI-generated
      disinformation incidents, creating public demand for regulatory action.
    </p>

    <h2>Key Provisions of the Law</h2>

    <h3>AI-Generated Content Labelling</h3>
    <p>
      Any content generated or substantially modified by AI — including text, images, audio,
      and video — must be <strong>clearly labelled</strong> as AI-generated before distribution.
      This applies to media organisations, social platforms, and businesses publishing
      AI-generated content. Failure to label is an enforcement violation.
    </p>

    <h3>Disclosure of AI Agents</h3>
    <p>
      Businesses deploying AI agents in customer-facing roles (chatbots, virtual assistants,
      automated call systems) must <strong>disclose to users</strong> that they are interacting
      with an AI system rather than a human. The law prohibits deliberate deception about
      AI identity in commercial or public service contexts.
    </p>

    <h3>Human Oversight for High-Risk AI</h3>
    <p>
      High-risk AI applications — defined to include healthcare diagnostics, credit scoring,
      employment decisions, law enforcement, and critical infrastructure control — must maintain
      <strong>human oversight mechanisms</strong>: a qualified human must be able to review,
      override, or halt AI decisions that affect individuals' rights or safety.
    </p>

    <h3>Regulatory Sandbox</h3>
    <p>
      The law establishes a <strong>regulatory sandbox scheme</strong> allowing selected
      AI projects to operate under relaxed requirements during a defined trial period,
      subject to enhanced monitoring and mandatory incident reporting. This provides a
      controlled path for innovative applications to demonstrate safety before full
      regulatory compliance is required.
    </p>

    <h3>Extraterritorial Reach</h3>
    <p>
      The law applies to any entity — Vietnamese or foreign — whose AI systems or services
      are deployed in Vietnam or whose AI-generated content reaches Vietnamese users.
      This puts international AI companies serving the Vietnamese market on notice that
      compliance is not optional.
    </p>

    <h2>The "Management for Development" Philosophy</h2>
    <p>
      Vietnam's law uses the phrase <strong>"management for development"</strong> to frame
      its regulatory philosophy: regulation should enable and channel AI development, not
      stifle it. The approach is risk-proportionate — lighter touch for low-risk applications,
      heavier requirements for high-risk uses — deliberately designed to avoid over-regulation
      of AI innovation.
    </p>

    <h2>Comparison with the EU AI Act</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Risk Classification</span>
        <span className="news-spec-value">Both use tiered risk-based approach</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Prohibited AI</span>
        <span className="news-spec-value">EU: explicit prohibited list; Vietnam: principles-based</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Labelling</span>
        <span className="news-spec-value">Both require AI-generated content disclosure</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Enforcement</span>
        <span className="news-spec-value">EU: up to €35M fines; Vietnam: still establishing enforcement body</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Sandbox</span>
        <span className="news-spec-value">Both include regulatory sandbox provisions</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Extraterritorial</span>
        <span className="news-spec-value">Both apply to foreign entities serving domestic users</span>
      </div>
    </div>

    <h2>What It Means for Businesses in SEA</h2>
    <p>
      Vietnam's AI law creates immediate compliance obligations for any business:
    </p>
    <ul>
      <li>Deploying AI chatbots or virtual agents for Vietnamese customers</li>
      <li>Using AI to generate marketing content distributed in Vietnam</li>
      <li>Operating AI-powered credit, HR, or healthcare systems in Vietnam</li>
      <li>Publishing AI-generated news, analysis, or media consumed by Vietnamese users</li>
    </ul>
    <p>
      For regional enterprises, Vietnam is now the compliance floor for ASEAN AI governance —
      any AI deployment standard that satisfies Vietnam's law will likely meet or exceed
      requirements in other SEA markets that have not yet legislated.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Vietnam's first-mover status in SEA AI legislation will define the regional
        legislative template. Other ASEAN members — Indonesia, Philippines, Thailand —
        are watching how Vietnam enforces this law and what business impact it creates.
        Early compliance is a competitive advantage: companies that build AI governance
        processes now will be positioned to operate across SEA as more countries adopt
        similar legislation through 2026 and 2027.
      </p>
    </div>
  </article>
);

export default VietnamAILaw;
